import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { extractTourDateMeta, formatTourDateRange, normalizeToDateKey } from './calendarHelpers';

/**
 * Load tour invites for the signed-in user from Firestore.
 * Supports `tourInvites` collection and legacy `users.pendingTourInvites`.
 *
 * @param {import('firebase/firestore').Firestore} db
 * @param {string | null | undefined} uid
 * @param {string | null | undefined} email
 */
export async function loadTourInvitesForUser(db, uid, email) {
  /** @type {Array<Record<string, unknown>>} */
  const invites = [];
  const seen = new Set();

  const addInvite = (row, id) => {
    const ownerId = String(row.ownerId || row.inviterId || '').trim();
    const tourId = String(row.tourId || row.sourceTourId || '').trim();
    if (!tourId) return;
    const key = `${ownerId}:${tourId}:${id}`;
    if (seen.has(key)) return;
    seen.add(key);
    invites.push({
      id,
      ownerId,
      tourId,
      tourName: String(row.tourName || row.name || 'Shared tour').trim() || 'Shared tour',
      ownerName: String(row.ownerName || row.inviterName || row.inviterEmail || 'Tour owner').trim(),
      inviteeEmail: String(row.inviteeEmail || '').trim().toLowerCase(),
      inviteeId: String(row.inviteeId || '').trim(),
      status: String(row.status || 'pending').trim().toLowerCase(),
      invitedAt: row.invitedAt || row.createdAt || null,
      days: Array.isArray(row.days) ? row.days : null,
      message: String(row.message || '').trim(),
    });
  };

  if (uid) {
    try {
      const snap = await getDocs(query(collection(db, 'tourInvites'), where('inviteeId', '==', uid)));
      snap.forEach((d) => addInvite(d.data() || {}, d.id));
    } catch (err) {
      console.warn('loadTourInvites inviteeId', err);
    }
  }

  const emailLower = String(email || '').trim().toLowerCase();
  if (emailLower) {
    try {
      const snap = await getDocs(query(collection(db, 'tourInvites'), where('inviteeEmail', '==', emailLower)));
      snap.forEach((d) => addInvite(d.data() || {}, d.id));
    } catch (err) {
      console.warn('loadTourInvites inviteeEmail', err);
    }
  }

  if (uid) {
    try {
      const userSnap = await getDoc(doc(db, 'users', uid));
      const pending = userSnap.data()?.pendingTourInvites;
      if (Array.isArray(pending)) {
        pending.forEach((row, index) => {
          if (!row || typeof row !== 'object') return;
          addInvite(row, String(row.id || `pending-${index}`));
        });
      }
    } catch (err) {
      console.warn('loadTourInvites user.pendingTourInvites', err);
    }
  }

  return invites
    .filter((row) => !['declined', 'revoked', 'cancelled'].includes(String(row.status || '').toLowerCase()))
    .sort((a, b) => {
      const aTime = a.invitedAt?.toMillis?.() || a.invitedAt?.seconds * 1000 || 0;
      const bTime = b.invitedAt?.toMillis?.() || b.invitedAt?.seconds * 1000 || 0;
      return bTime - aTime;
    });
}

/**
 * @param {import('firebase/firestore').Firestore} db
 * @param {Record<string, unknown>} invite
 */
export async function fetchSharedTourSnapshot(db, invite) {
  const tourId = String(invite?.tourId || '').trim();
  if (!tourId) return null;

  try {
    const sharedSnap = await getDoc(doc(db, 'sharedTours', tourId));
    if (sharedSnap.exists()) {
      const data = sharedSnap.data() || {};
      return {
        tourName: data.tourName || invite.tourName,
        days: Array.isArray(data.days) ? data.days : [],
        ownerId: data.ownerId || invite.ownerId,
        ownerName: data.ownerName || invite.ownerName,
      };
    }
  } catch (err) {
    console.warn('fetchSharedTourSnapshot sharedTours', err);
  }

  const ownerId = String(invite?.ownerId || '').trim();
  if (ownerId) {
    try {
      const ownerSnap = await getDoc(doc(db, 'users', ownerId));
      const folder = ownerSnap.data()?.folders?.[tourId];
      if (folder?.tour?.days) {
        return {
          tourName: folder.tour.name || folder.name || invite.tourName,
          days: folder.tour.days,
          ownerId,
          ownerName: invite.ownerName,
          eventStartDate: folder.tour.eventStartDate,
          eventEndDate: folder.tour.eventEndDate,
        };
      }
    } catch (err) {
      console.warn('fetchSharedTourSnapshot owner folders', err);
    }
  }

  if (Array.isArray(invite?.days) && invite.days.length) {
    return {
      tourName: invite.tourName,
      days: invite.days,
      ownerId: invite.ownerId,
      ownerName: invite.ownerName,
    };
  }

  return null;
}

/** @param {Record<string, unknown>} invite @param {Record<string, unknown> | null} snapshot */
export function buildSharedTourFolder(invite, snapshot) {
  const ownerId = String(invite?.ownerId || snapshot?.ownerId || 'unknown').trim();
  const sourceTourId = String(invite?.tourId || '').trim();
  const days = Array.isArray(snapshot?.days) ? snapshot.days : [];
  const name = String(snapshot?.tourName || invite?.tourName || 'Shared tour').trim() || 'Shared tour';
  const folderId = `shared-${ownerId}-${sourceTourId}`;

  return {
    id: folderId,
    name,
    tour: {
      name,
      days,
      eventStartDate: snapshot?.eventStartDate || null,
      eventEndDate: snapshot?.eventEndDate || null,
      linkedPostId: null,
      linkedPostSummary: null,
      interDayLegModes: [],
    },
    shared: true,
    ownerId,
    ownerName: String(snapshot?.ownerName || invite?.ownerName || 'Tour owner').trim(),
    sourceTourId,
    inviteId: String(invite?.id || '').trim(),
  };
}

/** @param {ReturnType<typeof buildSharedTourFolder>} folder */
export function enrichSharedInviteRow(invite, folder) {
  const meta = extractTourDateMeta(folder);
  return {
    invite,
    folder,
    meta,
    dateLabel: formatTourDateRange(meta.firstDate, meta.lastDate),
    ownerName: folder.ownerName || invite.ownerName || 'Tour owner',
    statusLabel: String(invite.status || 'pending').toLowerCase() === 'accepted' ? 'Accepted' : 'Invited',
  };
}

/** @param {Array<{ scheduledDate?: unknown }>} days */
export function sharedTourHasMissingDates(days) {
  return (days || []).some((day) => !normalizeToDateKey(day?.scheduledDate));
}
